import React from "react";
import { Navigate } from "react-router-dom";
import TechniqueSchematic, { getTechniqueCaption, getTechniqueMotionGuide } from "../components/TechniqueSchematic";
import { useAuth } from "../context/AuthContext";

/**
 * /dev/schematics — visual reference for all technique schematics.
 *
 * Phase 9 (Feb 27 2026): this used to be a public route open to
 * anyone who knew the URL. It's an internal QA / design surface and
 * should not be reachable from production for end users. We now gate
 * it behind `user.is_admin`; everyone else is bounced to the home
 * page silently. (We use a redirect rather than a 404 so engineers
 * who hit it while not logged in can simply sign in and come back.)
 */
const CATEGORIES = [
  "primer",
  "color_corrector",
  "foundation",
  "contour",
  "bronzer",
  "blush",
  "highlighter",
  "brows",
  "eyeshadow",
  "eyeliner",
  "mascara",
  "lip_liner",
  "lipstick",
  "lip_gloss",
  "setting_powder",
];

export default function SchematicGallery() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen trubeaute-gradient pt-12 pb-20 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-serif italic text-3xl sm:text-5xl text-[#2C2123] leading-tight mb-3 text-center">
          Technique schematics
        </h1>
        <p className="text-center text-[#5C4B51] mb-10 max-w-xl mx-auto">
          A visual reference for every makeup zone — where to apply, and the specific motion that brings it to life.
        </p>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="schematic-gallery-grid"
        >
          {CATEGORIES.map((cat) => {
            const guide = getTechniqueMotionGuide(cat);
            return (
              <div
                key={cat}
                className="bg-white border border-[#EADDD7] rounded-2xl p-4 flex flex-col items-center soft-shadow"
                data-testid={`schematic-card-${cat}`}
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
                  {cat.replace(/_/g, " ")}
                </div>
                <div className="bg-gradient-to-br from-[#FFF6F2] to-[#F5EFEB] rounded-xl border border-[#EADDD7] p-2 w-full">
                  <TechniqueSchematic
                    category={cat}
                    label={cat}
                    className="aspect-[5/6] w-full"
                  />
                </div>
                <div className="mt-3 text-[13px] italic text-[#5C4B51] text-center leading-snug">
                  {getTechniqueCaption(cat)}
                </div>
                <div className="mt-3 self-stretch text-[11px] uppercase tracking-[0.18em] text-[#8A7D82]">
                  Tool · {guide.tool}
                </div>
                <ol className="mt-2 self-stretch space-y-1.5">
                  {guide.steps.map((s, i) => (
                    <li
                      key={`${cat}-${i}`}
                      className="flex items-start gap-2 text-[12.5px] text-[#3D2F33] leading-snug"
                    >
                      <span className="shrink-0 mt-0.5 h-4 w-4 rounded-full bg-[#9B6A6C] text-white text-[10px] flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
